import { AxiosError } from 'axios';
import axios from 'utils/axios.config';
import { types, flow, Instance } from 'mobx-state-tree';
import { withAlertStore } from 'components/Core/CustomAlert/store';
import { ErrorData, IResponse, StatusType, StatusTypeReq, TranslatesObject } from 'types/common';
import {
  IPointsCategoryReqData,
  IPointsCategoryDataWithTranslations,
  CategoriesEntity,
  IProductCategoryReqData,
  IProductCategoryDataWithTranslations,
  IPointsCategoryPatchReq,
  IPointsCategoryStatusReq,
  IPointsCategoryStatusError,
} from './types';
import { FallbackCategoryRequiredError } from './errors';

const { model, frozen, maybeNull } = types;

export const CategoriesStore = model('CategoriesStore', {
  pointsCategories: maybeNull(frozen<IPointsCategoryReqData[]>()),
  pointsCategoryData: maybeNull(frozen<IPointsCategoryDataWithTranslations>()),
  productCategories: maybeNull(frozen<IProductCategoryReqData[]>()),
  productCategoryData: maybeNull(frozen<IProductCategoryDataWithTranslations>()),
})
  .extend(withAlertStore)
  .actions((self) => ({
    setPointsCategories(data: IPointsCategoryReqData[] | null) {
      self.pointsCategories = data;
    },
    setPointsCategoryData(data: IPointsCategoryDataWithTranslations | null) {
      self.pointsCategoryData = data;
    },
    setProductCategories(data: IProductCategoryReqData[] | null) {
      self.productCategories = data;
    },
    setProductCategoryData(data: IProductCategoryDataWithTranslations | null) {
      self.productCategoryData = data;
    },
  }))
  // GENERAL HANDLING
  .actions((self) => ({
    errorHandling({ response }: AxiosError<IResponse<ErrorData>>) {
      const msg = response ? response?.data.message || 'Internal server error' : 'Application error';
      self.alertStore.showAlert(msg);
      throw new Error(msg);
    },
  }))
  // REQUESTS API
  .actions((self) => ({
    getCategoriesData: flow(function* (entity: CategoriesEntity) {
      switch (entity) {
        case 'points-categories':
          yield axios
            .get<IResponse<IPointsCategoryReqData[]>>(`point/categories/list-with-translates-count`)
            .then(({ data: { data } }) => self.setPointsCategories(data))
            .catch(self.errorHandling);
          break;
        case 'product-categories':
          yield axios
            .get<IResponse<IProductCategoryReqData[]>>(`product/categories`)
            .then(({ data: { data } }) => self.setProductCategories(data))
            .catch(self.errorHandling);
          break;
        default:
          return;
      }
    }),
    getProductCategoryData: flow(function* (id: string) {
      yield axios
        .get<IResponse<IProductCategoryDataWithTranslations>>(`product/categories/${id}`)
        .then(({ data: { data } }) => self.setProductCategoryData(data))
        .catch(self.errorHandling);
    }),
    patchProductCategoryBasic: flow(function* (fields: { categoryName?: string; status?: StatusType }) {
      if (!self.productCategoryData) return;
      yield axios
        .patch<IResponse<IProductCategoryDataWithTranslations>>(
          `product/categories/${self.productCategoryData.id}`,
          fields
        )
        .then(({ data: { data } }) => self.setProductCategoryData(data))
        .catch(self.errorHandling);
    }),
    patchProductCategoryTranslations: flow(function* (translations: TranslatesObject) {
      if (!self.productCategoryData) return;
      yield axios
        .patch<IResponse<IProductCategoryDataWithTranslations>>(
          `product/categories/${self.productCategoryData.id}/translations`,
          { translations }
        )
        .then(({ data: { data } }) => self.setProductCategoryData(data))
        .catch(self.errorHandling);
    }),
    getPointsCategoryData: flow(function* (id: string) {
      yield axios
        .get<IResponse<IPointsCategoryDataWithTranslations>>(`point/categories/${id}`)
        .then(({ data: { data } }) => self.setPointsCategoryData(data))
        .catch(self.errorHandling);
    }),
    /**
     * The category form from the category page, multipart because the images
     * travel as files (MOB1-2112). Status is not part of it — the switch above
     * the form saves on its own through changeCategoryStatus. Returns the
     * saved entity so the form can reset onto it.
     */
    patchPointsCategory: flow(function* (fields: IPointsCategoryPatchReq) {
      if (!self.pointsCategoryData) return null;
      const body = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined) body.append(key, value);
      });
      return yield axios
        .patch<IResponse<IPointsCategoryDataWithTranslations>>(`point/categories/${self.pointsCategoryData.id}`, body, {
          // The instance defaults to application/json, under which axios would
          // serialise the FormData to JSON; the browser fills in the boundary.
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(({ data: { data } }) => {
          self.setPointsCategoryData(data);
          return data;
        })
        .catch(self.errorHandling);
    }),
    patchPointsCategoryTranslations: flow(function* (translations: TranslatesObject) {
      if (!self.pointsCategoryData) return;
      yield axios
        .patch<IResponse<IPointsCategoryDataWithTranslations>>(
          `point/categories/translations/${self.pointsCategoryData.id}`,
          { translations }
        )
        .then(({ data: { data } }) => {
          self.setPointsCategoryData(data);
        })
        .catch(self.errorHandling);
    }),

    /**
     * Point category status from the list row or the category page. A category
     * that is the last Active one of some points is refused by the backend
     * with 409 unless `fallbackCategoryId` names where those points go; the
     * list row has nowhere to pick it, so that case surfaces as
     * FallbackCategoryRequiredError for the caller to phrase, instead of the
     * generic error alert. Whatever is open — the list, the page or both —
     * is updated from the response.
     */
    changeCategoryStatus: flow(function* ({ id, status, fallbackCategoryId }: IPointsCategoryStatusReq) {
      yield axios
        .patch<IResponse<IPointsCategoryDataWithTranslations | null>>(`point/category-status/${id}`, {
          status,
          fallbackCategoryId,
        })
        .then(({ data: { data } }) => {
          if (self.pointsCategories) {
            self.setPointsCategories(
              self.pointsCategories.map((item) => (item.id === id ? { ...item, status } : item))
            );
          }
          if (self.pointsCategoryData?.id === id) {
            // The entity when the endpoint returns it, else at least the status.
            self.setPointsCategoryData(data || { ...self.pointsCategoryData, status });
          }
        })
        .catch((error: AxiosError<IPointsCategoryStatusError>) => {
          if (error.response?.status === 409) {
            throw new FallbackCategoryRequiredError(error.response.data?.errors?.orphanPointsCount || 0);
          }
          return self.errorHandling(error as AxiosError<IResponse<ErrorData>>);
        });
      return id;
    }),
    changeProductCategoryStatus: flow(function* ({ id, status }: StatusTypeReq) {
      yield axios
        .patch(`product/categories/${id}`, { status })
        .then(() => {
          if (self.productCategories) {
            self.setProductCategories(
              self.productCategories.map((item) => (item.id === id ? { ...item, status } : item))
            );
          }
        })
        .catch(self.errorHandling);
      return id;
    }),
  }))
  // KEYWORDS CRUD — separate block so `self` already exposes getProductCategoryData,
  // which each mutation calls to refresh keywordItems after writing to the table.
  .actions((self) => ({
    createProductCategoryKeyword: flow(function* (payload: { name: string; weight?: number }) {
      if (!self.productCategoryData) return;
      const id = self.productCategoryData.id;
      yield axios.post(`product/categories/${id}/keywords`, payload).catch(self.errorHandling);
      yield self.getProductCategoryData(id);
    }),
    updateProductCategoryKeyword: flow(function* (keywordId: string, payload: { name?: string; weight?: number }) {
      if (!self.productCategoryData) return;
      const id = self.productCategoryData.id;
      yield axios.patch(`product/categories/${id}/keywords/${keywordId}`, payload).catch(self.errorHandling);
      yield self.getProductCategoryData(id);
    }),
    deleteProductCategoryKeyword: flow(function* (keywordId: string) {
      if (!self.productCategoryData) return;
      const id = self.productCategoryData.id;
      yield axios.delete(`product/categories/${id}/keywords/${keywordId}`).catch(self.errorHandling);
      yield self.getProductCategoryData(id);
    }),
  }));

export type ICategoriesStore = Instance<typeof CategoriesStore>;
